import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { documentUrl, useDocumentHtml, type DocumentHtml } from '../hooks/useDocuments';
import { loadPdf, renderPage, renderPageToImage } from '../lib/pdf';
import { Doodle } from './Doodle';
import { documentKind, isWordDocument, type TeacherDocument } from '@td/shared';

/**
 * Showing a document, rather than asking the browser to.
 *
 * The first version handed PDFs to an iframe and let the browser's viewer deal
 * with them, which produced a grey box and a broken-file icon. An embedded PDF
 * depends on the browser's plugin policy and on the page's content-security
 * rules, and neither is ours to rely on. So pdf.js reads the file and we paint
 * it — the same thing Drive and Dropbox do, and for the same reason.
 *
 * Both sizes show a *page*, not a crop of one. A thumbnail that cuts the
 * bottom off the form is not a preview of the form, and a viewer that hands
 * back a tall scrolling strip is not how anybody expects to be shown a
 * document. So everything here is shaped like paper and fitted inside its box.
 */

/** A sheet of Letter, as a ratio. Everything page-shaped uses this. */
export const PAGE_RATIO = 8.5 / 11;

/* ------------------------------------------------------------ thumbnails */

/** The whole of page one, small. */
export function DocumentThumbnail({ document: doc }: { document: TeacherDocument }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  /*
   * A Word card shows the document too. Once a .docx has been converted there
   * is nothing special about it — it is HTML, and HTML draws small like
   * anything else. Shared with the reader through the query cache, and the
   * conversion itself is stored after the first time.
   */
  const html = useDocumentHtml(doc);

  useEffect(() => {
    if (doc.contentType !== 'application/pdf') return;

    let alive = true;

    // Wide enough that the small print on a form is still legible once shrunk.
    renderPageToImage(documentUrl(doc), 1, 500)
      .then((url) => {
        if (alive) setSrc(url);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
    };
  }, [doc]);

  if (doc.contentType.startsWith('image/')) {
    // `contain`, not `cover`: the whole photograph, letterboxed, rather than
    // the middle of it.
    return (
      <img
        src={documentUrl(doc)}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full bg-white object-contain"
      />
    );
  }

  if (doc.contentType === 'application/pdf') {
    if (failed) return <Placeholder>This PDF could not be drawn — open it to download</Placeholder>;

    return src ? (
      <img src={src} alt="" draggable={false} className="h-full w-full bg-white object-contain" />
    ) : (
      <Waiting>Drawing…</Waiting>
    );
  }

  if (isWordDocument(doc.contentType)) {
    if (html.isPending) return <Waiting>Reading…</Waiting>;

    if (html.data?.converted) {
      return <WordPageThumbnail html={html.data.html} />;
    }

    return <Placeholder>An older Word file — open it to download</Placeholder>;
  }

  return <Placeholder>{documentKind(doc.contentType)}</Placeholder>;
}

/**
 * A converted Word document, shrunk to a page.
 *
 * There is no pagination in converted HTML — Word's page breaks do not survive
 * the conversion, and nothing in a browser reintroduces them. So this lays the
 * document out at the width of a real page and shows the top of it at page
 * proportions, which is the same thing the eye is looking for: is this the
 * permission slip or the newsletter?
 *
 * Measured rather than assumed, because the card is whatever width the grid
 * gives it, and a hard-coded scale was only ever right at one window size.
 */
function WordPageThumbnail({ html }: { html: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);

  /** A page is 8.5in at 96dpi; the sheet is laid out at that and then shrunk. */
  const PAGE_PX = 816;

  useLayoutEffect(() => {
    const measure = () => {
      const width = box.current?.clientWidth ?? 0;
      if (width > 0) setScale(width / PAGE_PX);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (box.current) observer.observe(box.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={box} className="h-full w-full overflow-hidden bg-white">
      <div
        aria-hidden="true"
        className="document-html pointer-events-none origin-top-left"
        style={{ width: PAGE_PX, padding: 64, transform: `scale(${scale})` }}
        // Sanitised on the server before it was stored.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function Waiting({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-linen/40">
      <span className="text-[11px] text-stone">{children}</span>
    </div>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-linen/60 p-3 text-center">
      <Doodle name="paperclip" className="mb-1 h-8 w-8 text-clay" />
      <span className="text-[11px] leading-snug text-stone">{children}</span>
    </div>
  );
}

/* ---------------------------------------------------------------- reader */

/**
 * A PDF, a page at a time, whole.
 *
 * The first version drew every page into a scrolling strip, which is what
 * Trevor was looking at when he said it did not open "how it's supposed to be
 * shown" — and he was right. A document is read a page at a time, and a page
 * is read whole. So exactly one page is drawn, scaled to fit the space it has,
 * and there are arrows.
 *
 * Fitted to the box in both directions. Fitting to the width alone is what
 * produces the endless strip: a portrait page is taller than any window, so
 * the height has to be the constraint whenever it is the tighter one.
 */
export function PdfReader({ url, className = '' }: { url: string; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const canvasHost = useRef<HTMLDivElement>(null);

  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState<string | null>(null);

  // A new document starts at its first page rather than wherever the last one
  // was left.
  useEffect(() => setPage(1), [url]);

  useLayoutEffect(() => {
    const measure = () => {
      const node = box.current;
      if (node) setSize({ width: node.clientWidth, height: node.clientHeight });
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (box.current) observer.observe(box.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (size.width === 0 || size.height === 0) return;

    let alive = true;

    void (async () => {
      try {
        const pdf = await loadPdf(url);
        if (!alive) return;
        setPages(pdf.numPages);

        const wanted = Math.min(page, pdf.numPages);
        const proxy = await pdf.getPage(wanted);
        const base = proxy.getViewport({ scale: 1 });

        /*
         * Whichever of the two runs out first. The page keeps its own
         * proportions — a legal-size form and a square scan both come out
         * right — and neither dimension is allowed to overflow the box.
         */
        const width = Math.floor(
          Math.min(size.width, (size.height * base.width) / base.height) - 8,
        );

        const { canvas, width: w, height: h } = await renderPage(pdf, wanted, width);
        if (!alive) return;

        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        canvas.className = 'mx-auto bg-white shadow-soft';

        canvasHost.current?.replaceChildren(canvas);
      } catch {
        if (alive) {
          setError('This PDF could not be opened. Download it and it will open in your viewer.');
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [url, page, size]);

  if (error) {
    return (
      <div className={`flex items-center justify-center p-6 text-center ${className}`}>
        <p className="text-sm text-walnut">{error}</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${className}`}>
      <div ref={box} className="flex min-h-0 flex-1 items-center justify-center bg-linen/40 p-2">
        <div ref={canvasHost} />
        {pages === 0 && <p className="text-sm text-stone">Opening…</p>}
      </div>

      {pages > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-2 border-t border-sand bg-cream px-3 py-1.5">
          <button
            className="btn-soft"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            ‹ Back
          </button>
          <span className="text-xs text-stone">
            Page {Math.min(page, pages)} of {pages}
          </span>
          <button
            className="btn-soft"
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            disabled={page >= pages}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A Word document, on a sheet of paper.
 *
 * Word's own pagination does not survive the conversion and cannot be
 * reconstructed, so this is one continuous sheet rather than numbered pages —
 * but it is laid out at the width of real paper with real margins, which is
 * what makes it read as a document instead of as a web page. It scrolls,
 * because the alternative is inventing page breaks that would fall in the
 * wrong places.
 */
export function WordReader({
  document: doc,
  html,
  className = '',
}: {
  document: TeacherDocument;
  html: DocumentHtml | undefined;
  className?: string;
}) {
  if (!html) {
    return (
      <div className={`flex items-center justify-center p-6 ${className}`}>
        <p className="text-sm text-stone">Reading the document…</p>
      </div>
    );
  }

  if (!html.converted) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 text-center ${className}`}>
        <Doodle name="paperclip" className="mb-2 h-10 w-10 text-clay" />
        <p className="text-sm text-walnut">
          This one could not be read here — it is likely an older <code>.doc</code> rather than a{' '}
          <code>.docx</code>. Download it and it will open in Word.
        </p>
      </div>
    );
  }

  return (
    <div className={`overflow-auto bg-linen/40 p-4 ${className}`}>
      <article
        className="document-html mx-auto bg-white shadow-soft"
        aria-label={doc.name}
        // 8.5 inches at 96dpi, with an inch of margin: a sheet of paper.
        style={{ width: 816, maxWidth: '100%', padding: 72 }}
        // Sanitised server-side, by the allowlist that has forty-eight checks
        // against it, before it was ever stored.
        dangerouslySetInnerHTML={{ __html: html.html }}
      />
    </div>
  );
}
